// CRUD admin de profesores.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, AlertCircle, Loader2, X, FileText, Percent, Camera, Upload } from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listProfesores, createProfesor, updateProfesor, deleteProfesor,
  subirFotoProfesor, eliminarFotoProfesor,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

// SS7.8.bucket — validaciones cliente para selección de foto
const FOTO_MAX_BYTES = 5 * 1024 * 1024;
const FOTO_MIME_OK = new Set(['image/jpeg', 'image/png', 'image/webp']);

const FORM_VACIO = {
  nombre_publico: '', bio_corta_es: '', bio_corta_en: '', bio_corta_ru: '', bio_corta_fr: '',
  especialidades: '', foto_url: '', activo: true,
  instagram: '', facebook: '', mostrar_redes: false,
};

export default function AdminProfesores() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await listProfesores();
      setItems(r.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const onBorrar = async (item) => {
    const definitivo = !item.activo;
    const msg = definitivo
      ? t('admin.profesores.confirmar_borrar_definitivo', { nombre: item.nombre_publico })
      : t('admin.confirmar_desactivar', { nombre: item.nombre_publico });
    if (!window.confirm(msg)) return;
    try {
      await deleteProfesor(item.id, definitivo); cargar();
    } catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  const aFormulario = (p) => ({
    ...FORM_VACIO,
    ...p,
    especialidades: Array.isArray(p.especialidades) ? p.especialidades.join(', ') : '',
  });

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.profesores.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.profesores.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.profesores.col.nombre')}</th>
              <th>{t('admin.profesores.col.especialidades')}</th>
              <th>{t('admin.profesores.col.activo')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className={!p.activo ? 'inactivo' : ''}>
                <td>{p.nombre_publico}</td>
                <td>{(p.especialidades || []).join(', ')}</td>
                <td>{p.activo ? '✓' : '—'}</td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar(aFormulario(p))} aria-label={t('admin.editar')} title={t('admin.editar')}>
                    <Edit2 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/profesores/${p.id}/datos-fiscales`)}
                    aria-label={t('admin.profesores.datos_fiscales_titulo')}
                    title={t('admin.profesores.datos_fiscales_titulo')}
                  >
                    <FileText size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/profesores/${p.id}/descuentos`)}
                    aria-label={t('admin.profesores.descuentos.titulo')}
                    title={t('admin.profesores.descuentos.titulo')}
                  >
                    <Percent size={16} />
                  </button>
                  <button type="button" onClick={() => onBorrar(p)} aria-label={t('admin.borrar')} title={t('admin.borrar')}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <ProfesorModal item={editar} onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />
      )}
    </div>
  );
}

function ProfesorModal({ item, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const editando = !!item.id;

  // Estado de la foto:
  //   - selectedFile: nuevo archivo elegido (pendiente de subir al guardar)
  //   - previewUrl:    DataURL para mostrar antes de subir
  //   - eliminarFotoFlag: usuario pulsó "Eliminar foto"
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [eliminarFotoFlag, setEliminarFotoFlag] = useState(false);
  const [fotoError, setFotoError] = useState(null);
  const fileInputRef = useRef(null);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSeleccionFoto = (e) => {
    setFotoError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!FOTO_MIME_OK.has(file.type)) {
      setFotoError(t('admin.profesores.f.error_tipo'));
      e.target.value = '';
      return;
    }
    if (file.size > FOTO_MAX_BYTES) {
      setFotoError(t('admin.profesores.f.error_tamano'));
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    setEliminarFotoFlag(false);
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const onPedirEliminarFoto = () => {
    if (!window.confirm(t('admin.profesores.f.confirmar_eliminar'))) return;
    setEliminarFotoFlag(true);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const especialidadesArr = (form.especialidades || '')
        .split(',').map((s) => s.trim()).filter(Boolean);
      // Payload sin foto_url — la foto se gestiona vía endpoints dedicados.
      const payload = {
        nombre_publico: form.nombre_publico,
        bio_corta_es: form.bio_corta_es || null,
        bio_corta_en: form.bio_corta_en || null,
        bio_corta_ru: form.bio_corta_ru || null,
        bio_corta_fr: form.bio_corta_fr || null,
        especialidades: especialidadesArr,
        activo: !!form.activo,
        instagram: form.instagram || null,
        facebook: form.facebook || null,
        mostrar_redes: !!form.mostrar_redes,
      };
      let profesorId = item.id;
      if (editando) {
        await updateProfesor(profesorId, payload);
      } else {
        const creado = await createProfesor(payload);
        profesorId = creado?.id;
      }
      // Foto: primero borrar (si flag), luego subir si hay archivo nuevo.
      if (eliminarFotoFlag && profesorId && form.foto_url) {
        try { await eliminarFotoProfesor(profesorId); } catch (e2) {
          if (!(e2 instanceof ApiError && e2.code === 'SIN_FOTO')) throw e2;
        }
      }
      if (selectedFile && profesorId) {
        await subirFotoProfesor(profesorId, selectedFile);
      }
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.crear_nuevo')}</h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo full">
              <label>{t('admin.profesores.f.nombre_publico')}</label>
              <input type="text" value={form.nombre_publico} onChange={setCampo('nombre_publico')} required />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.profesores.f.especialidades')}</label>
              <input type="text" value={form.especialidades} onChange={setCampo('especialidades')}
                     placeholder="Hatha, Vinyasa, Meditación" />
            </div>
            <div className="modal-campo full profesor-foto-section">
              <label>{t('admin.profesores.f.foto_titulo')}</label>
              {!editando && (
                <p className="profesor-foto-hint">{t('admin.profesores.f.foto_solo_edicion')}</p>
              )}
              {editando && (
                <div className="profesor-foto-row">
                  <div className="profesor-foto-preview">
                    {previewUrl ? (
                      <img src={previewUrl} alt="" />
                    ) : eliminarFotoFlag ? (
                      <div className="profesor-foto-placeholder">
                        <Camera size={28} strokeWidth={1.5} />
                      </div>
                    ) : form.foto_url ? (
                      <img src={form.foto_url} alt="" />
                    ) : (
                      <div className="profesor-foto-placeholder">
                        <Camera size={28} strokeWidth={1.5} />
                      </div>
                    )}
                  </div>
                  <div className="profesor-foto-acciones">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={onSeleccionFoto}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-mini"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={14} strokeWidth={1.75} />
                      {form.foto_url || previewUrl
                        ? t('admin.profesores.f.cambiar_foto')
                        : t('admin.profesores.f.subir_foto')}
                    </button>
                    {(form.foto_url || previewUrl) && !eliminarFotoFlag && (
                      <button
                        type="button"
                        className="btn-secondary btn-mini btn-danger-soft"
                        onClick={onPedirEliminarFoto}
                      >
                        <Trash2 size={14} strokeWidth={1.75} />
                        {t('admin.profesores.f.eliminar_foto')}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {fotoError && (
                <div className="profesor-foto-error">
                  <AlertCircle size={14} /><span>{fotoError}</span>
                </div>
              )}
            </div>
            <div className="modal-campo full">
              <label>{t('admin.profesores.f.bio_es')}</label>
              <textarea rows={2} value={form.bio_corta_es || ''} onChange={setCampo('bio_corta_es')} />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.profesores.f.bio_en')}</label>
              <textarea rows={2} value={form.bio_corta_en || ''} onChange={setCampo('bio_corta_en')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.profesores.f.instagram')}</label>
              <input type="text" value={form.instagram || ''} onChange={setCampo('instagram')}
                     placeholder="@handle" />
            </div>
            <div className="modal-campo">
              <label>{t('admin.profesores.f.facebook')}</label>
              <input type="text" value={form.facebook || ''} onChange={setCampo('facebook')}
                     placeholder="page.url" />
            </div>
            <div className="modal-campo full">
              <label>
                <input type="checkbox" checked={!!form.mostrar_redes}
                       onChange={(e) => setForm((f) => ({ ...f, mostrar_redes: e.target.checked }))} />
                {' '}{t('admin.profesores.f.mostrar_redes')}
              </label>
            </div>
            <div className="modal-campo full">
              <label>
                <input type="checkbox" checked={!!form.activo}
                       onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} />
                {' '}{t('admin.profesores.f.activo')}
              </label>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
